/**
 * CONFILEX — Ambient Sound Engine v3 (Vraies œuvres piano classique)
 *
 * Lecteur HTML5 Audio diffusant de vrais enregistrements piano libres de
 * droits (domaine public : Chopin, Debussy, Satie). Fichiers hébergés
 * localement dans /audio/piano/ pour fiabilité maximale.
 *
 * Caractéristiques :
 *  - Fade-in / fade-out doux (2s) via WebAudio GainNode
 *  - Crossfade automatique entre les morceaux
 *  - Rotation aléatoire (jamais 2× le même morceau de suite)
 *  - Plusieurs "ambiances" = des sélections curatées de morceaux
 *  - Unlock synchrone pour Safari/iOS
 */

export const AMBIENCES = [
  {
    id: "bibliotheque-juridique",
    label: "Bibliothèque juridique premium",
    description: "Chopin, Debussy — lecture sereine.",
    inspiration: "Frédéric Chopin · Claude Debussy",
    tracks: [
      { file: "/audio/piano/chopin-nocturne-op9-2.mp3", composer: "Chopin", title: "Nocturne op. 9 n°2" },
      { file: "/audio/piano/debussy-childrens-corner.mp3", composer: "Debussy", title: "Children's Corner — extrait" },
      { file: "/audio/piano/chopin-nocturne-op15-2.mp3", composer: "Chopin", title: "Nocturne op. 15 n°2" },
    ],
  },
  {
    id: "piano-neoclassique",
    label: "Piano néoclassique moderne",
    description: "Satie, Debussy — fluidité contemporaine.",
    inspiration: "Erik Satie · Claude Debussy",
    tracks: [
      { file: "/audio/piano/satie-gymnopedie-1.mp3", composer: "Satie", title: "Gymnopédie n°1" },
      { file: "/audio/piano/debussy-childrens-corner.mp3", composer: "Debussy", title: "Children's Corner — extrait" },
      { file: "/audio/piano/satie-prelude-enginhard.mp3", composer: "Satie", title: "Prélude d'Enginhard" },
    ],
  },
  {
    id: "cabinet-confidentiel",
    label: "Cabinet confidentiel",
    description: "Chopin — nocturnes intimistes.",
    inspiration: "Frédéric Chopin",
    tracks: [
      { file: "/audio/piano/chopin-nocturne-op9-2.mp3", composer: "Chopin", title: "Nocturne op. 9 n°2" },
      { file: "/audio/piano/chopin-nocturne-op15-2.mp3", composer: "Chopin", title: "Nocturne op. 15 n°2" },
      { file: "/audio/piano/chopin-nocturne-op37-1.mp3", composer: "Chopin", title: "Nocturne op. 37 n°1" },
    ],
  },
  {
    id: "concentration-elegante",
    label: "Concentration élégante",
    description: "Satie minimaliste — concentration.",
    inspiration: "Erik Satie",
    tracks: [
      { file: "/audio/piano/satie-gymnopedie-1.mp3", composer: "Satie", title: "Gymnopédie n°1" },
      { file: "/audio/piano/satie-prelude-enginhard.mp3", composer: "Satie", title: "Prélude d'Enginhard" },
    ],
  },
  {
    id: "piano-minimaliste",
    label: "Piano minimaliste",
    description: "Satie Gymnopédies — méditation.",
    inspiration: "Erik Satie",
    tracks: [
      { file: "/audio/piano/satie-gymnopedie-1.mp3", composer: "Satie", title: "Gymnopédie n°1" },
      { file: "/audio/piano/satie-prelude-enginhard.mp3", composer: "Satie", title: "Prélude d'Enginhard" },
    ],
  },
  {
    id: "salon-prive",
    label: "Salon privé institutionnel",
    description: "Chopin Nocturnes — soir feutré.",
    inspiration: "Frédéric Chopin Nocturnes",
    tracks: [
      { file: "/audio/piano/chopin-nocturne-op37-1.mp3", composer: "Chopin", title: "Nocturne op. 37 n°1" },
      { file: "/audio/piano/chopin-nocturne-op9-2.mp3", composer: "Chopin", title: "Nocturne op. 9 n°2" },
      { file: "/audio/piano/chopin-nocturne-op15-2.mp3", composer: "Chopin", title: "Nocturne op. 15 n°2" },
    ],
  },
  {
    // Ambiance "Sérénité spirituelle" — pour les phases d'accueil, attente, IA
    // en cours, upload, transfert, mise en attente. Volume nominal abaissé,
    // ducking automatique en cas de parole.
    //
    // ⚠️ Le fichier audio référencé n'est PAS livré (droits d'auteur).
    //   Pour activer cette ambiance, déposez votre piste licenciée à :
    //     /app/frontend/public/audio/sereinity/time-with-god.mp3
    //   Si le fichier est absent, la lecture échouera silencieusement et
    //   l'utilisateur pourra simplement choisir une autre ambiance.
    id: "serenite-spirituelle",
    label: "Sérénité spirituelle",
    description: "Time With God — accompagnement contemplatif.",
    inspiration: "Time With God",
    nominalVolume: 0.18,   // volume max 18% — strictement discret
    duckingEnabled: true,  // active le ducking auto sur parole
    tracks: [
      { file: "/audio/sereinity/time-with-god.mp3",
        composer: "—", title: "Time With God" },
    ],
  },
];

export function getAmbience(id) {
  return AMBIENCES.find((a) => a.id === id) || AMBIENCES[0];
}

const FADE_DURATION = 2.0; // secondes

export class AmbientEngine {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.activeAudio = null;     // élément <audio> en cours
    this.activeSource = null;    // MediaElementSourceNode actif
    this.activeGain = null;      // GainNode du lecteur actif
    this.elementGainMap = new WeakMap(); // 1 MediaElementSourceNode par élément max
    this.currentAmbience = null;
    this.targetVolume = 0.4;
    this.running = false;
    this.currentTrackIdx = -1;
    this.playedHistory = [];
    this.endedHandler = null;
    this.duckMultiplier = 1; // 1 = pas de ducking, <1 = abaissé
  }

  _ensureContext() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0;
    this.masterGain.connect(this.ctx.destination);
  }

  /** Unlock synchrone (à appeler dans un handler de clic). */
  unlock() {
    this._ensureContext();
    if (!this.ctx) return false;
    if (this.ctx.state === "suspended") {
      const p = this.ctx.resume();
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[CONFILEX ambient] resume rejected", err);
          }
        });
      }
    }
    try {
      const src = this.ctx.createBufferSource();
      src.buffer = this.ctx.createBuffer(1, 1, 22050);
      src.connect(this.ctx.destination);
      src.start(0);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[CONFILEX ambient] silent unlock buffer failed", err);
      }
    }
    return true;
  }

  setVolume(v) {
    this.targetVolume = Math.max(0, Math.min(1, v));
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(t);
    this.masterGain.gain.linearRampToValueAtTime(
      this.running ? this._effectiveVolume() : 0,
      t + 0.4
    );
  }

  /** Volume effectif (volume nominal × multiplicateur de ducking, capé éventuellement). */
  _effectiveVolume() {
    const ducked = this.duckMultiplier ?? 1;
    const cap = this.nominalVolumeCap ?? 1;
    const base = Math.min(this.targetVolume, cap);
    return Math.max(0, Math.min(1, base * ducked));
  }

  /**
   * Abaisse le volume à un multiplicateur du volume nominal (ducking).
   * Utilisé quand l'utilisateur parle, que l'IA prend la parole ou
   * qu'une saisie active demande la concentration.
   *
   * @param {number} multiplier  0.0 (mute) à 1.0 (nominal). Défaut: 0.25
   * @param {number} ms          durée de la rampe en ms. Défaut: 350
   */
  duck(multiplier = 0.25, ms = 350) {
    this.duckMultiplier = Math.max(0, Math.min(1, multiplier));
    if (!this.ctx || !this.masterGain) return;
    const t = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(t);
    this.masterGain.gain.linearRampToValueAtTime(
      this.running ? this._effectiveVolume() : 0,
      t + Math.max(0.05, ms / 1000),
    );
  }

  /** Rétablit le volume nominal en douceur. */
  unduck(ms = 600) {
    this.duckMultiplier = 1;
    if (!this.ctx || !this.masterGain) return;
    const t = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(t);
    this.masterGain.gain.linearRampToValueAtTime(
      this.running ? this._effectiveVolume() : 0,
      t + Math.max(0.1, ms / 1000),
    );
  }

  /** Choisit le prochain morceau (jamais 2× le même de suite si possible). */
  _pickNextTrackIndex(tracks) {
    if (!tracks || tracks.length <= 1) return 0;
    let idx;
    let safety = 0;
    do {
      idx = Math.floor(Math.random() * tracks.length);
      safety += 1;
    } while (idx === this.currentTrackIdx && safety < 6);
    return idx;
  }

  /** Charge un morceau et le démarre avec fade-in. Coupe le précédent en fade-out. */
  _playTrack(track) {
    if (!this.ctx) return;
    const trackUrl = typeof track === "string" ? track : track.file;
    const trackMeta = typeof track === "string" ? { file: track } : track;
    const audio = new Audio(trackUrl);
    audio.crossOrigin = "anonymous";
    audio.preload = "auto";
    audio.dataset.confilexAmbient = "1";
    audio.volume = 1.0;

    // Source WebAudio dérivée du <audio>
    let source;
    try {
      source = this.ctx.createMediaElementSource(audio);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[CONFILEX ambient] createMediaElementSource failed", err);
      }
      return;
    }
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    source.connect(gain).connect(this.masterGain);

    const now = this.ctx.currentTime;
    gain.gain.linearRampToValueAtTime(1.0, now + FADE_DURATION);

    // Coupe l'ancien en fade-out, puis détruit
    if (this.activeAudio && this.activeGain) {
      const prevAudio = this.activeAudio;
      const prevGain = this.activeGain;
      prevGain.gain.cancelScheduledValues(now);
      prevGain.gain.linearRampToValueAtTime(prevGain.gain.value, now);
      prevGain.gain.linearRampToValueAtTime(0, now + FADE_DURATION);
      setTimeout(() => {
        try {
          prevAudio.pause();
          prevAudio.src = "";
          prevAudio.load();
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[CONFILEX ambient] cleanup failed", err);
          }
        }
      }, (FADE_DURATION + 0.3) * 1000);
    }

    this.activeAudio = audio;
    this.activeSource = source;
    this.activeGain = gain;
    this.activeTrack = trackMeta;
    if (typeof this._onTrackChange === "function") {
      try {
        this._onTrackChange(trackMeta);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[CONFILEX ambient] onTrackChange listener threw", err);
        }
      }
    }

    // À la fin du morceau, passer au suivant
    if (this.endedHandler) {
      // détaché auto (chaque <audio> a son propre handler à l'init)
    }
    audio.addEventListener("ended", () => this._onTrackEnded(), { once: true });

    // Lecture
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise
        .then(() => {
          this.playbackBlocked = false;
          if (typeof this._onPlaybackStateChange === "function") {
            try {
              this._onPlaybackStateChange({ blocked: false });
            } catch (err) {
              if (process.env.NODE_ENV !== "production") {
                console.warn("[CONFILEX ambient] onPlaybackStateChange listener threw (resume)", err);
              }
            }
          }
        })
        .catch((err) => {
          this.playbackBlocked = true;
          if (typeof this._onPlaybackStateChange === "function") {
            try {
              this._onPlaybackStateChange({ blocked: true, error: err });
            } catch (e) {
              if (process.env.NODE_ENV !== "production") {
                console.warn("[CONFILEX ambient] onPlaybackStateChange listener threw (blocked)", e);
              }
            }
          }
          if (process.env.NODE_ENV !== "production") {
            console.warn("[CONFILEX ambient] audio.play() rejected (needs gesture?)", err);
          }
        });
    }
  }

  _onTrackEnded() {
    if (!this.running || !this.currentAmbience) return;
    const tracks = this.currentAmbience.tracks;
    this.currentTrackIdx = this._pickNextTrackIndex(tracks);
    this._playTrack(tracks[this.currentTrackIdx]);
  }

  async start(ambience, volume) {
    this._ensureContext();
    if (!this.ctx) return false;
    if (this.ctx.state === "suspended") {
      try {
        await this.ctx.resume();
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[CONFILEX ambient] AudioContext resume needs a user gesture", err);
        }
      }
    }
    this.currentAmbience = ambience;
    if (typeof volume === "number") this.targetVolume = volume;
    // Spec : certaines ambiances ont un volume nominal plafonné (ex: Time
    // With God à 18% — sérénité spirituelle). On cape `targetVolume` au
    // nominal de l'ambiance courante pour respecter la règle « toujours en
    // arrière-plan, jamais au même niveau que la voix ».
    this.nominalVolumeCap = typeof ambience.nominalVolume === "number"
      ? Math.max(0, Math.min(1, ambience.nominalVolume))
      : 1;

    // Fade-in du master vers le volume effectif (capé + ducké éventuellement)
    const t = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(t);
    this.masterGain.gain.linearRampToValueAtTime(this._effectiveVolume(), t + FADE_DURATION);

    this.running = true;
    const tracks = ambience.tracks || [];
    if (!tracks.length) return false;
    this.currentTrackIdx = this._pickNextTrackIndex(tracks);
    this._playTrack(tracks[this.currentTrackIdx]);
    return true;
  }

  stop({ fade = true } = {}) {
    this.running = false;
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(t);
    this.masterGain.gain.linearRampToValueAtTime(this.masterGain.gain.value, t);
    this.masterGain.gain.linearRampToValueAtTime(0, t + (fade ? FADE_DURATION * 0.75 : 0.05));
    // Coupe la lecture <audio> après le fade-out
    setTimeout(() => {
      try {
        if (this.activeAudio) this.activeAudio.pause();
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[CONFILEX ambient] pause failed", err);
        }
      }
    }, (fade ? FADE_DURATION * 0.75 : 0.05) * 1000 + 100);
  }

  pause() { this.stop({ fade: true }); }
  isActive() { return this.running; }

  /** Renvoie les métadonnées du morceau en cours et la progression (0-1). */
  getNowPlaying() {
    if (!this.activeAudio || !this.activeTrack) return null;
    const dur = this.activeAudio.duration || 0;
    const cur = this.activeAudio.currentTime || 0;
    return {
      ...this.activeTrack,
      duration: dur,
      currentTime: cur,
      progress: dur > 0 ? Math.min(1, cur / dur) : 0,
    };
  }

  /** Permet à l'UI d'être notifiée des changements de piste. */
  onTrackChange(cb) { this._onTrackChange = cb; }

  /** Permet à l'UI d'être notifiée si la lecture est bloquée. */
  onPlaybackStateChange(cb) { this._onPlaybackStateChange = cb; }
}

let engineInstance = null;
export function getEngine() {
  if (!engineInstance) engineInstance = new AmbientEngine();
  return engineInstance;
}
